import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GlobalLayout from '../GlobalLayout';
import { getPages, createPage, deletePage, getApps, switchPage } from '../../services/admin_api';

const TreeNode = ({ node, index, siblings, onMove, onDelete, onAddChild, onEdit }) => {
    const isFirst = index === 0;
    const isLast = index === siblings.length - 1;

    return (
        <div style={{ marginLeft: '2rem', marginTop: '0.5rem' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff'
            }}>
                <span style={{ fontWeight: 'bold', marginRight: 'auto' }}>
                    {node.label || node.title || node.name || `Page #${node.id}`} <small style={{ fontWeight: 'normal', color: '#666' }}>(Rank: {node.rank})</small>
                </span>

                <div style={{ display: 'flex', gap: '0.2rem' }}>
                    <button
                        disabled={isFirst}
                        onClick={() => onMove(node, 'up', siblings)}
                        style={{ cursor: isFirst ? 'not-allowed' : 'pointer' }}
                    >
                        ↑
                    </button>
                    <button
                        disabled={isLast}
                        onClick={() => onMove(node, 'down', siblings)}
                        style={{ cursor: isLast ? 'not-allowed' : 'pointer' }}
                    >
                        ↓
                    </button>
                </div>

                <button onClick={() => onAddChild(node)}>+ Sub-page</button>
                <button onClick={() => onEdit(node)}>Éditer</button>
                <button onClick={() => onDelete(node)} style={{ color: 'red' }}>&times;</button>
            </div>

            {node.Children && node.Children.length > 0 && (
                <div style={{ borderLeft: '2px solid #eee' }}>
                    {node.Children.map((child, idx) => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            index={idx}
                            siblings={node.Children}
                            onMove={onMove}
                            onDelete={onDelete}
                            onAddChild={onAddChild}
                            onEdit={onEdit}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const PageManagerList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [apps, setApps] = useState([]);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const selectedAppId = searchParams.get('appId') || '';

    // Quick Add Form
    const [isAdding, setIsAdding] = useState(false);
    const [addingParentId, setAddingParentId] = useState(null);
    const [newPageName, setNewPageName] = useState('');

    useEffect(() => {
        fetchApps();
    }, []);

    useEffect(() => {
        if (selectedAppId) {
            fetchPages();
        } else {
            setPages([]);
        }
    }, [selectedAppId]);

    const fetchApps = async () => {
        try {
            const data = await getApps(1, 1000); // Fetch all apps
            if (data && data.data) {
                setApps(data.data);
            } else if (Array.isArray(data)) {
                setApps(data);
            }
        } catch (err) {
            console.error("Failed to fetch apps", err);
        }
    };

    const fetchPages = async () => {
        setLoading(true);
        try {
            const data = await getPages(selectedAppId);
            // API returns tree structure directly now, with capitalized Children
            if (Array.isArray(data)) {
                // Sort root level and recursive children just in case, though API might do it.
                // The user example shows them sorted by rank.
                // Let's implement a recursive sort helper.
                const sortTree = (nodes) => {
                    if (!nodes) return [];
                    nodes.sort((a, b) => (a.rank || 0) - (b.rank || 0));
                    nodes.forEach(node => {
                        if (node.Children) node.Children = sortTree(node.Children);
                    });
                    return nodes;
                }
                setPages(sortTree(data));
            } else {
                setPages([]);
            }
        } catch (err) {
            console.error("Failed to fetch pages", err);
        } finally {
            setLoading(false);
        }
    };

    // processPagesToTree removed as API returns tree

    const handleAppChange = (e) => {
        const nextAppId = e.target.value;
        const nextSearchParams = new URLSearchParams(searchParams);

        if (nextAppId) {
            nextSearchParams.set('appId', nextAppId);
        } else {
            nextSearchParams.delete('appId');
        }

        setSearchParams(nextSearchParams, { replace: true });
    };

    const handleAddClick = (parentNode) => {
        setAddingParentId(parentNode ? parentNode.id : null);
        setNewPageName('');
        setIsAdding(true);
    };

    const handleSubmitNewPage = async (e) => {
        e.preventDefault();
        try {
            // Calculate rank: last rank + 1
            // We need to find the children of the parent to determine next rank
            // This is a bit tricky with Just the tree, but backend handles default usually.
            // But spec says we manage rank.
            // Let's assume we put it at the end.
            const siblings = addingParentId
                ? (findNode(pages, addingParentId)?.Children || [])
                : pages;

            const maxRank = siblings.reduce((max, p) => Math.max(max, p.rank || 0), 0);

            await createPage({
                app_id: parseInt(selectedAppId, 10),
                parent_id: addingParentId,
                label: newPageName,
                rank: maxRank + 1
            });
            setIsAdding(false);
            fetchPages();
        } catch (err) {
            alert('Failed to create page');
        }
    };

    const findNode = (nodes, id) => {
        for (let node of nodes) {
            if (node.id === id) return node;
            if (node.Children) {
                const found = findNode(node.Children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const handleDelete = async (node) => {
        if (!window.confirm(`Delete page "${node.title || node.name}"?`)) return;
        try {
            await deletePage(node.id);
            fetchPages();
        } catch (err) {
            alert('Failed to delete page');
        }
    };

    const handleMove = async (node, direction, siblings) => {
        const index = siblings.findIndex(s => s.id === node.id);
        if (index === -1) return;

        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= siblings.length) return;

        const sibling = siblings[swapIndex];

        // Swap ranks
        const nodeNewRank = sibling.rank;
        const siblingNewRank = node.rank;

        try {
            // Update both
            await switchPage(node.id, sibling.id);
            fetchPages();
        } catch (err) {
            alert('Failed to move page');
        }
    };

    const handleEditPage = (node) => {
        const editPath = selectedAppId
            ? `/pages/edit/${node.id}?appId=${encodeURIComponent(selectedAppId)}`
            : `/pages/edit/${node.id}`;

        navigate(editPath);
    };

    return (
        <GlobalLayout>
            <h1>Page Management</h1>

            <div style={{ marginBottom: '1rem' }}>
                <label>Select App: </label>
                <select value={selectedAppId} onChange={handleAppChange}>
                    <option value="">-- Choose an App --</option>
                    {apps.map(app => (
                        <option key={app.id} value={app.id}>{app.name || app.code}</option>
                    ))}
                </select>
            </div>

            {selectedAppId && (
                <div>
                    {loading ? <p>Loading...</p> : (
                        <div>
                            {pages.length === 0 ? <p>No pages found for this app.</p> : (
                                pages.map((node, idx) => (
                                    <TreeNode
                                        key={node.id}
                                        node={node}
                                        index={idx}
                                        siblings={pages}
                                        onMove={handleMove}
                                        onDelete={handleDelete}
                                        onAddChild={handleAddClick}
                                        onEdit={handleEditPage}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {isAdding && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}>
                    <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', minWidth: '300px' }}>
                        <h3>Add Page</h3>
                        <form onSubmit={handleSubmitNewPage}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label>Page Name:</label>
                                <input
                                    type="text"
                                    value={newPageName}
                                    onChange={e => setNewPageName(e.target.value)}
                                    required
                                    style={{ width: '100%', padding: '0.5rem', marginTop: '0.5rem' }}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                <button type="button" onClick={() => setIsAdding(false)}>Cancel</button>
                                <button type="submit">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </GlobalLayout>
    );
};

export default PageManagerList;
